/**
 * Version Polling Service
 * Layer 2: Client-side version polling for stale chunk detection
 *
 * Polls /version.json every 5 minutes to detect when a new deployment has occurred.
 * When a new version is detected, sets a flag to trigger UI notification.
 */

interface VersionInfo {
  buildId: string;
  version: string;
  timestamp: string;
  env: string;
}

type VersionChangeCallback = (newVersion: VersionInfo, oldVersion: VersionInfo) => void;

class VersionPollingService {
  private pollingInterval: number = 1 * 60 * 1000; // 1 minute
  private intervalId: number | null = null;
  private currentBuildId: string | null = null;
  private currentVersion: VersionInfo | null = null;
  private callbacks: Set<VersionChangeCallback> = new Set();
  private isPolling: boolean = false;
  private lastCheckTime: number = 0;

  constructor() {
    // Get initial build ID from window (injected during build).
    const injectedBuildId = (window as any).__APP_BUILD_ID__ || null;
    this.currentBuildId = injectedBuildId;
    // Seed currentVersion so the first poll compares against the build-injected
    // buildId rather than blindly accepting whatever the server returns.
    // Without this, deployments that happen between page load and the first
    // poll go undetected (the first poll resets the baseline to the new value).
    if (injectedBuildId) {
      this.currentVersion = {
        buildId: injectedBuildId,
        version: '',
        timestamp: '',
        env: '',
      };
    }
  }

  /**
   * Start polling for version changes
   */
  start(): void {
    if (this.isPolling) {
      console.warn('[VersionPolling] Already polling');
      return;
    }

    console.log('[VersionPolling] Starting version polling (every 1 minute)');
    this.isPolling = true;

    // Initial check
    this.checkVersion();

    // Set up interval
    this.intervalId = window.setInterval(() => {
      this.checkVersion();
    }, this.pollingInterval);
  }

  /**
   * Stop polling
   */
  stop(): void {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isPolling = false;
    console.log('[VersionPolling] Stopped version polling');
  }

  /**
   * Check for version updates
   */
  private async checkVersion(): Promise<void> {
    try {
      this.lastCheckTime = Date.now();

      console.log('[VersionPolling] 🔍 Checking for updates...');

      // Fetch version.json with cache-busting query param
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        console.warn('[VersionPolling] ❌ Failed to fetch version.json:', response.status);
        return;
      }

      const versionInfo: VersionInfo = await response.json();
      console.log('[VersionPolling] 📦 Server version:', versionInfo);

      // Store first version
      if (!this.currentVersion) {
        this.currentVersion = versionInfo;
        this.currentBuildId = versionInfo.buildId;
        console.log('[VersionPolling] ✅ Initial version stored:', versionInfo);
        console.log('[VersionPolling] 🖥️  Client buildId:', (window as any).__APP_BUILD_ID__ || 'NOT SET');
        return;
      }

      // Check if build ID has changed
      console.log('[VersionPolling] 🔄 Comparing versions:');
      console.log('  🖥️  Client buildId:', this.currentBuildId);
      console.log('  ☁️  Server buildId:', versionInfo.buildId);

      if (versionInfo.buildId !== this.currentBuildId) {
        console.log('[VersionPolling] 🎉 NEW VERSION DETECTED!');
        console.log('  📅 Current:', this.currentVersion);
        console.log('  🆕 New:', versionInfo);

        // Notify all callbacks
        console.log('[VersionPolling] 📢 Notifying', this.callbacks.size, 'callback(s)');
        this.callbacks.forEach(callback => {
          try {
            callback(versionInfo, this.currentVersion!);
          } catch (error) {
            console.error('[VersionPolling] ❌ Callback error:', error);
          }
        });

        // Update current version
        const oldVersion = this.currentVersion;
        this.currentVersion = versionInfo;
        this.currentBuildId = versionInfo.buildId;

        // Emit custom event for other parts of the app
        window.dispatchEvent(new CustomEvent('app:version-changed', {
          detail: { newVersion: versionInfo, oldVersion }
        }));

        console.log('[VersionPolling] ✅ Update notification triggered!');
      } else {
        console.log('[VersionPolling] ✅ Versions match - no update needed');
      }

    } catch (error) {
      console.error('[VersionPolling] ❌ Error checking version:', error);
    }
  }

  /**
   * Register a callback for version changes
   */
  onVersionChange(callback: VersionChangeCallback): () => void {
    this.callbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
    };
  }

  /**
   * Get current version info
   */
  getCurrentVersion(): VersionInfo | null {
    return this.currentVersion;
  }

  /**
   * Get current build ID
   */
  getCurrentBuildId(): string | null {
    return this.currentBuildId;
  }

  /**
   * Check if polling is active
   */
  isActive(): boolean {
    return this.isPolling;
  }

  /**
   * Get time since last check (in milliseconds)
   */
  getTimeSinceLastCheck(): number {
    if (this.lastCheckTime === 0) return 0;
    return Date.now() - this.lastCheckTime;
  }

  /**
   * Manual version check (useful for testing or immediate check)
   */
  async forceCheck(): Promise<void> {
    await this.checkVersion();
  }

  /**
   * Set custom polling interval (in milliseconds)
   * Note: Requires restart to take effect
   */
  setPollingInterval(intervalMs: number): void {
    this.pollingInterval = intervalMs;
    if (this.isPolling) {
      this.stop();
      this.start();
    }
  }
}

// Singleton instance
export const versionPollingService = new VersionPollingService();

// Auto-start in production
if (import.meta.env.PROD) {
  console.log('[VersionPolling] 🚀 Production mode detected');
  console.log('[VersionPolling] ⏱️  Will start polling in 10 seconds...');

  // Start after a short delay to avoid blocking initial render
  setTimeout(() => {
    console.log('[VersionPolling] ⏰ 10 seconds elapsed, starting polling now');
    versionPollingService.start();
  }, 10000); // 10 seconds after app load
} else {
  console.log('[VersionPolling] 🔧 Development mode - polling NOT auto-started');
  console.log('[VersionPolling] 💡 To start manually, run: versionPollingService.start()');
}

export default versionPollingService;
